import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Star, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MembershipPage = () => {
  usePageTitle('Get Your Card', 'Choose your Advanta Plus membership plan and start saving today');
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/membership/plans`);
      setPlans(response.data);
    } catch (error) {
      toast.error("Failed to load plans");
    }
  };

  const handlePurchase = async (planId) => {
    setSelectedPlan(planId);
    setLoading(true);
    
    try {
      const affiliateCode = localStorage.getItem("advanta_affiliate");
      
      const response = await axios.post(
        `${API}/membership/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin,
          affiliate_code: affiliateCode || null
        },
        getAuthHeader()
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout");
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  // Annual ("1_year") plan removed from the storefront 2026-06-17. Existing
  // subscribers stay valid; this just hides the option from new purchases.
  const planOrder = ["1_week", "2_weeks", "1_month"];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-lg tracking-tight">A+</span>
            </div>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#D4AF37] text-sm tracking-widest uppercase">Membership Plans</span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mt-4">
            Choose Your <span className="italic text-[#D4AF37]">Pass</span>
          </h1>
          <p className="text-white/50 mt-4 max-w-xl mx-auto">
            One-time payment, no recurring fees. Start saving at 50+ partner locations across Costa del Sol.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-4 gap-6">
          {planOrder.map((planId, index) => {
            const plan = plans[planId];
            if (!plan) return null;
            
            const isBestValue = index === 3;
            
            return (
              <div 
                key={planId}
                className={`pricing-card rounded-2xl p-6 text-center relative overflow-hidden ${isBestValue ? 'featured' : ''}`}
                data-testid={`plan-card-${planId}`}
              >
                {isBestValue && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#F5DEB3] to-[#B8860B]" />
                )}
                
                {isBestValue && (
                  <span className="inline-block px-4 py-1 rounded-full bg-[#D4AF37] text-[#020617] text-xs font-bold tracking-wider uppercase mb-4">
                    Best Value
                  </span>
                )}
                
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-1">{plan.name}</h3>
                <p className="text-white/40 text-sm mb-6">{plan.duration_days} days</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#D4AF37]">€{plan.price}</span>
                </div>
                
                <ul className="text-white/60 text-sm space-y-3 mb-6 text-left">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#047857]" />
                    Digital Membership Card
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#047857]" />
                    10-20% Partner Discounts
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#047857]" />
                    Instant Activation
                  </li>
                </ul>
                
                <Button 
                  onClick={() => handlePurchase(planId)}
                  disabled={loading}
                  className={`w-full py-4 rounded ${isBestValue ? 'btn-gold' : 'btn-gold-outline'}`}
                  data-testid={`purchase-${planId}-btn`}
                >
                  {loading && selectedPlan === planId ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Select Plan"
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Features */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {[
            { icon: Star, title: "Premium Partners", desc: "Curated selection of the finest dining, shopping, and service providers" },
            { icon: Star, title: "Instant Access", desc: "Your digital membership card is ready immediately after purchase" },
            { icon: Star, title: "No Hidden Fees", desc: "One-time payment only, no subscriptions or recurring charges" },
          ].map((feature, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <feature.icon className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h4 className="font-['Playfair_Display'] text-lg text-white mb-2">{feature.title}</h4>
              <p className="text-white/50 text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
